// tslint:disable:no-commented-code
// tslint:disable:no-duplicate-string
// tslint:disable:only-arrow-functions
// tslint:disable:no-big-function
// tslint:disable:no-identical-functions
// tslint:disable:cognitive-complexity

import {
  ColumnConfiguration,
  RowConfiguration,
} from '@epicuro-next/ataraxia/grid-builder';
import { recommendationsPanelsLocaleKeys } from '@epicuro-next/constants/features/localization';
import { MediaType } from '@epicuro-next/models/engagement';
import {
  IDynamicBindableValue,
  IDynamicBindableValueCombination,
  IDynamicItem,
  IDynamicItemValue,
  IDynamicSimpleText,
} from '@epicuro-next/models/platform/dynamic-elements/dynamic-elements';
import { Product } from 'graphql-api/gql-api-interfaces';
import {
  ProductsOnTheWaySizesType,
  RecommendedItemsResponseType,
  RecommendedItemTypeProductsOnTheWay,
} from 'models-recommendation';

import { createInstanceWithChildren } from '../../../../utils/dynamic-items-utils';
import { IDynamicEventHandlerWithId } from '../../../../utils/eventhandler-from-action';
import { productDiscountLabel } from '../../../components/product-discount-label';
import {
  openConfigStripEventHandler,
  recommendationItemClickedEventHandler,
} from '../../../events';
import { SingleProductDataType } from '../../../types/single-product-data-type';

export type ProductsOnTheWayDataType = SingleProductDataType & {
  deliveryDate: string;
  sizes: ProductsOnTheWaySizesType[];
};

export type ProductsOnTheWayPayloadPropertiesType = {
  brandId: string;
  fromDate: string;
  toDate: string;
  filterMissingImages: boolean;
  addressId?: string;
  loadPrices?: boolean;
};

export function createApiRequestPayloadPropertiesProvider(providers: {
  daysIntoTheFuture: IDynamicItemValue;
  daysIntoThePast: IDynamicItemValue;
  shipToAddressId?: IDynamicItemValue;
  filterMissingImages: IDynamicItemValue;
  brandId: IDynamicItemValue;
}): IDynamicItemValue {
  return {
    values: [
      providers.brandId,
      providers.daysIntoTheFuture,
      providers.daysIntoThePast,
      providers.shipToAddressId,
      providers.filterMissingImages,
    ],
    transform: function ([
      brandId,
      daysIntoTheFuture,
      daysIntoThePast,
      shipToAddressId,
      filterMissingImages,
    ]: [
      string,
      number | string,
      number | string,
      string | undefined | null,
      boolean,
    ]) {
      function setTimeAndReturnISODateString(
        date: Date,
        hours: number,
        minutes: number,
        seconds: number,
        milliseconds: number,
      ): string {
        const firstPartLength = 'yyyy-MM-ddT'.length;
        const datePart = date.toISOString().substring(0, firstPartLength);
        function padWithZero(value: number, nrOfPlaces: number = 2): string {
          return value.toString().padStart(nrOfPlaces, '0');
        }
        return `${datePart}${padWithZero(hours)}:${padWithZero(
          minutes,
        )}:${padWithZero(seconds)}.${padWithZero(milliseconds, 3)}Z`;
      }

      const fromDate = new Date();
      if (typeof daysIntoThePast === 'string') {
        daysIntoThePast = parseInt(daysIntoThePast, 10);
      }
      const pastDateToSet = fromDate.getDate() - daysIntoThePast;
      fromDate.setDate(pastDateToSet);

      const fromDateStr = setTimeAndReturnISODateString(fromDate, 0, 0, 0, 0);

      const toDate = new Date(new Date().getTime());
      if (typeof daysIntoTheFuture === 'string') {
        daysIntoTheFuture = parseInt(daysIntoTheFuture, 10);
      }
      const toDateToSet = toDate.getDate() + daysIntoTheFuture;
      toDate.setDate(toDateToSet);
      toDate.setHours(23, 59, 59, 999);
      const toDateStr = setTimeAndReturnISODateString(toDate, 23, 59, 59, 999);

      const payloadProperties: ProductsOnTheWayPayloadPropertiesType = {
        brandId,
        fromDate: fromDateStr,
        toDate: toDateStr,
        filterMissingImages,
        loadPrices: true,
      };

      if (shipToAddressId !== undefined && shipToAddressId !== null) {
        payloadProperties.addressId = shipToAddressId;
      }

      return payloadProperties;
    }.toString(),
  };
}

export function productsOnTheWayApiResponseTransform() {
  return function (
    recommendedItemsResponse: RecommendedItemsResponseType<RecommendedItemTypeProductsOnTheWay>,
  ): ProductsOnTheWayDataType[] {
    const productsData: ProductsOnTheWayDataType[] = [];
    for (const itemInDataSet of recommendedItemsResponse.result.dataSet) {
      const item = itemInDataSet.item;
      const product = item.product;

      if (product === undefined || product === null) {
        // This should never be the case, BE should either return it or not.
        // At the time of writing it is a known issue that sometimes the product is not defined.
        continue;
      }
      const productData: ProductsOnTheWayDataType = {
        media: {
          mediaType: 'IMAGE' as MediaType,
          url: product.media?.[0]?.src?.[0]?.url ?? '',
        },
        configStrip: {
          overlayPath: [
            'cs',
            product.brandId as string,
            product.masterProduct?.productId ?? 'PARENT ID NA',
            product.productId as string,
          ],
        },
        product: {
          ...product,
          articleTitle: product.title,
        } as unknown as Product,
        deliveryDate: item.deliveryDate,
        sizes: item.sizes,
        correlationId: recommendedItemsResponse.operationId,
        servingConfig: recommendedItemsResponse.result.metadata.provider,
        providerId: recommendedItemsResponse.result.metadata.providerid,
        attributionToken: null,
        hash: recommendedItemsResponse.hash,
      };

      productsData.push(productData);
    }
    return productsData;
  };
}

export function addRequestedDeliveryDateAndQuantitiesToItemContent(
  template: {
    itemContentType: IDynamicItem;
    productTitle: IDynamicSimpleText;
    itemMediaPreview: IDynamicItem;
  },
  productDataPropertyName: string,
  providers: {
    requestedDeliveryDateFontSize:
      | IDynamicBindableValue
      | IDynamicBindableValueCombination;
    quantityFontSize: IDynamicBindableValue | IDynamicBindableValueCombination;
    showOrdinalSuffix: IDynamicBindableValue | IDynamicBindableValueCombination;
  },
) {
  const itemHeader = createInstanceWithChildren({
    contentTypeId: 'dynamic-collections-renderer',
    styles: {
      classes: [
        'd-flex',
        'flex-row',
        'justify-content-center',
        'align-items-center',
      ],
    },
    children: [template.productTitle],
  });

  const onClickEventHandlers: IDynamicEventHandlerWithId[] = [
    openConfigStripEventHandler('parent', productDataPropertyName),
    recommendationItemClickedEventHandler(
      {
        provider: 'id:square-panel-products-data-container',
        property: 'panelName',
      },
      {
        provider: 'id:square-panel-products-data-container',
        property: 'panelTrackingId',
      },
      {
        provider: 'parent',
        property: productDataPropertyName,
      },
    ),
  ];

  const sumQuantitiesPropertyName = 'summedQuantities';
  const quantityText: IDynamicItem = {
    id: 'products-on-the-way-quantity-text',
    contentTypeId: 'dynamic-elements-popover-wrapper',
    styles: {
      style: {
        color: 'black',
        bottom: '0px',
        // 2.5rem half the size of the internal content
        left: 'calc(50% - 2.5rem)',
        'font-size': {
          ...providers.quantityFontSize,
          transform: function (fontSize?: string): string {
            const defaultFontSize = '2rem';
            return Boolean(fontSize?.trim())
              ? fontSize ?? defaultFontSize
              : defaultFontSize;
          }.toString(),
        },
      },
      classes: ['position-absolute', 'ml-1', 'cursor-pointer'],
    },
    data: {
      [sumQuantitiesPropertyName]: {
        provider: 'parent',
        property: productDataPropertyName,
        transform: function (productData: ProductsOnTheWayDataType): string {
          const quantitiesSummed = productData.sizes
            .map((s) => s.quantity)
            .reduce(
              (previousValue, currentValue) => previousValue + currentValue,
              0,
            );
          return quantitiesSummed.toString();
        }.toString(),
      },
      sizes: {
        provider: 'parent',
        property: productDataPropertyName,
        transform: function (
          productData: ProductsOnTheWayDataType,
        ): ProductsOnTheWaySizesType[] {
          return productData.sizes;
        }.toString(),
      },
    },
    inputs: {
      triggers: 'hover',
      triggerContent: {
        contentTypeId: 'dynamic-elements-circle-text',
        styles: {
          style: {
            margin: 'auto',
          },
          classes: ['cursor-pointer'],
        },
        inputs: {
          text: {
            provider: 'parent',
            property: sumQuantitiesPropertyName,
          },
        },
      },
      popoverContent: {
        contentTypeId: 'dynamic-elements-grid-builder',
        styles: {
          variables: {
            '--grid-builder-gap': '0px',
            '--grid-builder-header-bg': 'var(--color-brand-primary)',
            '--grid-builder-header-color': 'var(--color-brand-on-primary)',
            '--grid-builder-header-corner-bg':
              'var(--color-brand-hover-primary, #303f9f)',
          },
        },
        data: {
          sortedColumns: {
            provider: 'parent',
            property: 'sizes',
            transform: function (sizes: ProductsOnTheWaySizesType[]): string[] {
              return sizes
                .map((size) => size.size ?? '')
                .filter((value, index, array) => array.indexOf(value) === index)
                .sort((a, b) =>
                  a.localeCompare(b, undefined, { numeric: true }),
                );
            }.toString(),
          },
          sortedRows: {
            provider: 'parent',
            property: 'sizes',
            transform: function (sizes: ProductsOnTheWaySizesType[]): string[] {
              return sizes
                .map((size) => size.width ?? '')
                .filter(
                  (value, index, array) => array.indexOf(value) === index,
                );
            }.toString(),
          },
        },
        inputs: {
          gridCornerClass:
            'd-flex align-items-center font-weight-bold justify-content-center h3 mb-0',
          gridTitle: {
            provider: 'parent',
            property: sumQuantitiesPropertyName,
          },
          columns: {
            provider: 'self',
            property: 'sortedColumns',
            transform: function (
              sortedColumns: string[],
            ): ColumnConfiguration[] {
              return sortedColumns.map((size) => ({
                columnName: size ?? '',
                bindProperty: size,
                columnHeaderClass: 'font-weight-bold px-1',
              }));
            }.toString(),
          },
          rows: {
            values: [
              {
                provider: 'parent',
                property: 'sizes',
              },
              {
                provider: 'self',
                property: 'sortedColumns',
              },
              {
                provider: 'self',
                property: 'sortedRows',
              },
            ],
            transform: function ([sizes, sortedColumns, sortedRows]: [
              ProductsOnTheWaySizesType[],
              string[],
              string[],
            ]) {
              const rows: RowConfiguration<{ [key: string]: number }>[] =
                sortedRows.map((width) => ({
                  rowTitle: width ?? '',
                  rowTitleClass: 'font-weight-bold mr-1',
                  data: sortedColumns.reduce((acc, column) => {
                    acc[column] =
                      sizes.find((fromSizes) => {
                        return (
                          fromSizes.size === column &&
                          (fromSizes.width ? fromSizes.width === width : true)
                        );
                      })?.quantity ?? 0;
                    return acc;
                  }, {} as { [key: string]: number }),
                }));
              return rows;
            }.toString(),
          },
          rowHeaderConfig: {
            rowHeaderWidth: '3rem',
          },
        },
      },
    },
    eventHandlers: {
      onClick: onClickEventHandlers,
    },
  };

  const deliveryDate: IDynamicSimpleText = {
    contentTypeId: 'dynamic-elements-simple-text',
    inputs: {
      text: {
        values: [
          {
            provider: 'parent',
            property: productDataPropertyName,
          },
          providers.showOrdinalSuffix,
        ],
        transform: function ([productData, showOrdinalSuffix]: [
          ProductsOnTheWayDataType,
          boolean,
        ]): string {
          const getMonthAbbr = (date: Date) => {
            const monthNames = [
              'Jan',
              'Feb',
              'Mar',
              'Apr',
              'May',
              'Jun',
              'Jul',
              'Aug',
              'Sep',
              'Oct',
              'Nov',
              'Dec',
            ];
            return monthNames[date.getMonth()];
          };

          const nextDeliveryDate = new Date(
            productData.deliveryDate.substring(0, 10),
          );

          const monthAbbr = getMonthAbbr(nextDeliveryDate);
          const dayInMonth = nextDeliveryDate.getDate();
          const ordinalSuffix = showOrdinalSuffix
            ? window.neoclesDynamicFramework.recommendationsPanels.methods.getNumberOrdinalSuffix(
                dayInMonth,
              )
            : '';

          const deliveryDateStr =
            `${monthAbbr} ${dayInMonth}${ordinalSuffix}`.toUpperCase();
          return deliveryDateStr;
        }.toString(),
      },
    },
    eventHandlers: {
      onClick: onClickEventHandlers,
    },
  };

  const deliveryDateContainer = createInstanceWithChildren({
    id: 'products-on-the-way-delivery-date-container',
    styles: {
      style: {
        color: 'black',
        top: '0px',
        right: '0px',
        'font-size': {
          ...providers.requestedDeliveryDateFontSize,
          transform: function (
            fontSize?: string | { key: string; value: string }[],
          ): string {
            const defaultFontSize = '1.2rem';
            if (typeof fontSize !== 'string' && fontSize?.[0]?.value) {
              return fontSize[0].value;
            }

            if (typeof fontSize === 'string') {
              return Boolean(fontSize?.trim())
                ? fontSize ?? defaultFontSize
                : defaultFontSize;
            }

            return defaultFontSize;
          }.toString(),
        },
      },
      classes: [
        'position-absolute',
        'mr-1',
        'd-flex',
        'flex-row',
        'cursor-pointer',
      ],
    },
    contentTypeId: 'dynamic-collections-renderer',
    children: [deliveryDate],
    attributes: {
      title: recommendationsPanelsLocaleKeys.requestedDeliveryDate,
    },
    eventHandlers: {
      onClick: onClickEventHandlers,
    },
  });

  const discountLabel: IDynamicItem = productDiscountLabel;

  const itemMediaPreviewWrapper = createInstanceWithChildren({
    id: 'products-on-the-way-item-wrapper',
    contentTypeId: 'dynamic-collections-renderer',
    styles: {
      classes: ['position-relative'],
    },
    children: [
      template.itemMediaPreview,
      deliveryDateContainer,
      quantityText,
      discountLabel,
    ],
  });

  template.itemContentType.children = [itemHeader, itemMediaPreviewWrapper];
}

export type SingleAddressFilterValue = {
  accountId: string;
  addressId?: string | null;
};
